import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import SEO from "@/components/SEO";

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    setLoading(true);
    const q = category !== "all" ? `?category=${encodeURIComponent(category)}` : "";
    api.get(`/blog${q}`).then(({ data }) => setPosts(data)).finally(() => setLoading(false));
  }, [category]);

  const categories = Array.from(new Set(posts.map((p) => p.category)));

  return (
    <div className="min-h-screen">
      <SEO
        title="IV Therapy Blog & Resources | IV Therapy Finder"
        description="Expert articles on IV therapy, NAD+, mobile infusion, recovery, immunity, and how to choose the right provider."
      />

      <section className="bg-gradient-to-b from-[#F3F4F1] to-[#FAFAFA] border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-2">IVTF Resources</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900 mb-3">The IV Therapy Journal</h1>
          <p className="text-stone-600 max-w-xl mx-auto">Evidence-based guides to IV therapy, mobile infusion, NAD+, hangover recovery, and more — written for patients, reviewed for accuracy.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {categories.length > 1 && (
          <div className="mb-6 flex flex-wrap gap-2">
            <CategoryPill active={category === "all"} onClick={() => setCategory("all")} label="All articles" />
            {categories.map((c) => (
              <CategoryPill key={c} active={category === c} onClick={() => setCategory(c)} label={c} />
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#064E3B]" /></div>
        ) : posts.length === 0 ? (
          <p className="text-center text-stone-500 py-12">No posts yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="group bg-white rounded-2xl border border-stone-200/70 overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-[#064E3B]/30 transition-all"
                data-testid={`blog-card-${p.slug}`}
              >
                <div className="aspect-[16/10] bg-stone-100 overflow-hidden">
                  <img src={p.hero_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-5">
                  <Badge variant="secondary" className="bg-[#064E3B]/5 text-[#064E3B] hover:bg-[#064E3B]/5 border-0 font-normal mb-2">{p.category}</Badge>
                  <h2 className="font-display text-lg font-semibold leading-tight text-stone-900 group-hover:text-[#064E3B] transition mb-2">{p.title}</h2>
                  <p className="text-sm text-stone-600 line-clamp-2 mb-3">{p.excerpt}</p>
                  <div className="flex items-center gap-3 text-xs text-stone-500">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.reading_minutes} min read</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.published_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function CategoryPill({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${active ? "bg-[#064E3B] text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-[#064E3B]/40"}`}
    >
      {label}
    </button>
  );
}
